import { FC, useEffect, useState, useRef, useCallback } from 'react';
import { SxProps } from '@mui/material';

import { Rooftop } from '@libs/models';
import { useForm } from '@carcareers/contexts';
import { AutocompleteSearch, SearchParams } from '@carcareers/components';
import { rooftopApi } from '@carcareers/store';

interface RooftopSelectProps {
  name: string;
  jobName?: string;
  clearJob?: boolean;
  defaultValue?: { label: string; value: Rooftop | number };
  sx?: SxProps;
}

export const RooftopSelect: FC<RooftopSelectProps> = ({ name, jobName = 'job', clearJob = false, defaultValue, sx }) => {
  const { setData, getData, setErrors, errors } = useForm<Rooftop>();
  const [localDefaultValue, setLocalDefaultValue] = useState(defaultValue);
  const [defaultLoaded, setDefaultLoaded] = useState(false);
  const hasInitializedRef = useRef(false);
  const userHasEditedRef = useRef(false);
  
  const [searchRooftop, { isLoading }] = rooftopApi.endpoints.rooftopSearch.useLazyQuery();

  const wrappedSearch = useCallback(async (params: SearchParams) => {
    userHasEditedRef.current = true; // User is searching/editing
    
    // Clear errors when user starts searching/editing
    if (errors && errors.length > 0) {
      setErrors(prevErrors => prevErrors.filter(error => error.property !== name));
    }
    
    try {
      const result = await searchRooftop(params);
      return result.data;
    } catch (error) {
      console.error('Search error:', error);
      return { items: [] };
    }
  }, [searchRooftop, errors, setErrors, name]);

  useEffect(() => {
    if (!hasInitializedRef.current && defaultValue?.value) {
      const currentValue = getData(name);
      
      if (!currentValue) {
        if (typeof defaultValue.value === 'number') {
          setData(name, defaultValue.value);
        } else if (typeof defaultValue.value === 'object' && defaultValue.value.id) {
          setData(name, defaultValue.value.id);
        }
        
        // Clear errors for this field when value is set
        if (errors && errors.length > 0) {
          setErrors(prevErrors => prevErrors.filter(error => error.property !== name));
        }
      }
      
      setLocalDefaultValue(defaultValue);
      
      if (typeof defaultValue.value === 'object' && defaultValue.value.name) {
        setDefaultLoaded(true);
      }
      
      hasInitializedRef.current = true;
    }
  }, [defaultValue, name, setData, getData, errors, setErrors]);

  useEffect(() => {
    if (hasInitializedRef.current && defaultValue && !userHasEditedRef.current) {
      setLocalDefaultValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (defaultValue && !defaultLoaded && typeof defaultValue.value === 'number') {
      searchRooftop({ q: defaultValue.value.toString(), page: 1, limit: 10 });
      setDefaultLoaded(true);
    }
  }, [defaultValue, searchRooftop, defaultLoaded]);

  useEffect(() => {
    if (clearJob && defaultLoaded) {
      setData(jobName, null);
    }
  }, [clearJob, jobName, setData, defaultLoaded]);

  return (
    <AutocompleteSearch
      name={name}
      loading={isLoading}
      search={wrappedSearch}
      label="Rooftop"
      defaultValue={localDefaultValue}
      sx={sx}
    />
  );
};

export default RooftopSelect;